import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { loadKitWebFonts } from '../../music24/promote/themes/kitWebFonts';
import { loadCoverPicture } from '../render/loadCoverPicture';
import { renderCoverDoc } from '../render/renderCoverDoc';

/** The limits of the API for the small picture of a lead (`leadThumbnailOf`). */
export const LEAD_THUMBNAIL_PX = 800;
export const LEAD_THUMBNAIL_MAX_BYTES = 300 * 1024;
/** The JPEG qualities to try, best first, until the file is under the limit. */
export const LEAD_THUMBNAIL_QUALITIES = [0.82, 0.7, 0.55, 0.4] as const;

/** The byte size of the data of a base64 data string. Pure. */
export function dataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const padding = base64.endsWith('==') ? 2 : Number(base64.endsWith('='));
  return Math.floor((base64.length * 3) / 4) - padding;
}

/**
 * The first data string under the byte limit, in the order given (best quality first), or null
 * when none fits. Pure.
 */
export function pickThumbnail(candidates: readonly string[], maxBytes = LEAD_THUMBNAIL_MAX_BYTES): string | null {
  return candidates.find((candidate) => dataUrlBytes(candidate) <= maxBytes) ?? null;
}

/**
 * The small JPEG of the design for the "Your cover is saved" email: the one renderer at 800 px,
 * with the watermark, as a base64 data string under 300 KB. Null when the browser cannot draw it
 * (the lead then goes with no picture).
 */
export async function makeLeadThumbnail(doc: CoverDoc): Promise<string | null> {
  try {
    await loadKitWebFonts();
    const picture = doc.background.kind === 'picture' ? await loadCoverPicture(doc.background.url) : null;
    const canvas = document.createElement('canvas');
    canvas.width = LEAD_THUMBNAIL_PX;
    canvas.height = LEAD_THUMBNAIL_PX;
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      return null;
    }
    renderCoverDoc(ctx, doc, LEAD_THUMBNAIL_PX, { picture }, { watermark: true });
    const picked = pickThumbnail(LEAD_THUMBNAIL_QUALITIES.map((quality) => canvas.toDataURL('image/jpeg', quality)));
    canvas.width = 0;
    canvas.height = 0;
    return picked;
  } catch (error) {
    console.error('Cover maker: the small picture could not be drawn', error);
    return null;
  }
}
